import { notify } from "front-commons/ds";
import { useDynamicLoading } from "front-commons/hooks";
import { useMemo } from "react";
import { manageBasketProductAmount } from "services/basket";
import { updateProductByPosId } from "services/import";
import { updateCartGTM } from "shared/gtm";
import useBasket from "stores/basket";
import useCustomer from "stores/customer";
import useDrawer from "stores/drawer";
import { UseProductSuggestionProps } from "./interfaces";

export function useProductSuggestion({
	posId,
	selectedDistribution,
	orderImportProductId,
	onAddSuggestion,
	handleRefetchItemList,
	data
}: UseProductSuggestionProps) {
	const { handleLoading } = useDynamicLoading<'loading' | 'waitingBasket'>();

	const {
		basketStore: { basket: basketData },
	} = useBasket();
	const { handleCloseAllDrawers } = useDrawer();
	const { handleHasPermission } = useCustomer();

  const hasPermission = useMemo(() => handleHasPermission('BASKET_EDIT'), []);
	const productBasket = useMemo(
		() =>
			basketData?.baskets?.find((basket) =>
				basket.products.find(
					(product) =>
						product.id === data?.id ||
						product.productsInCombo.find((productInCombo) => productInCombo.productId === data?.id),
				),
			),
		[basketData],
	);

	const dispatchGTM = (type: string, newQuantity: number) => {
		const { unitDiscount, finalPrice, distributorName } = selectedDistribution || {};

		const total = (finalPrice || 0) * (newQuantity || (data?.suggestedQuantity || 0));

		const products = [
			{
				ean13: data?.ean13,
				description: data?.description,
				price: finalPrice || 0,
				quantity: newQuantity || data?.suggestedQuantity,
				brandName: data?.brandName,
				categoryName: data?.categoryName,
				businessUnitName: data?.businessUnitName,
				industryName: data?.industryName,
				discount: unitDiscount,
				affiliation: distributorName,
				combo: false,
				suggestionGroupName: data?.suggestionGroupName,
			},
		];

		updateCartGTM(type, total, products);
	};

	const handleAddSuggestion = async () => {
		if (!data) return;

		try {
			handleLoading().set(['loading']);
			if (orderImportProductId) {
				await updateProductByPosId({
					removed: false,
					quantity: data?.suggestedQuantity,
					distributorId: selectedDistribution?.toString(),
					orderImportProductId,
				});
				handleRefetchItemList?.();
				handleCloseAllDrawers();
			} else {
				await manageBasketProductAmount({
					posId, data: {
						quantity: data.suggestedQuantity,
						productId: data.id,
						distributorId: selectedDistribution?.distributorId || "",
						suggestionGroupId: data.suggestionGroupId,
						suggestionGroupName: data.suggestionGroupName,
					}
				});
				onAddSuggestion?.();
				dispatchGTM('add_to_cart', data.suggestedQuantity);
			}
			handleLoading().set(['waitingBasket']);
			notify.positive({ description: 'Sugestão adicionada com sucesso!' });
		} catch (error) {
			console.log(error);
			notify.negative({
				description: 'Não foi possível adicionar essa sugestão ao seu carrinho! Por favor, tente novamente.',
			});
		} finally {
			handleLoading().remove(['loading', 'waitingBasket']);
		}
	};

	return {
		handleAddSuggestion,
		handleLoading,
		hasPermission,
		productBasket,
	}
}